import { Box, Typography, Divider, useMediaQuery, useTheme } from "@mui/material";
import LucaEyeAnimation from "./LucaAnimation/LucaAnimasion";
import { useAmbient } from "../../../components/Motion/useAmbient";
import { Reveal } from "../../../components/Motion";
import { GradientText } from "../../../components/shared";

const TechSkills = () => {
  // One eye, placed per breakpoint. Both placements used to render at once with
  // one hidden by `display: none`, so two frame loops ran for a single visible eye.
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  // The gradient sweeps under the headings loop forever; pause them while the
  // section is offscreen. (They used to animate background-position, which
  // repainted the text every frame; GradientText slides a layer instead, which
  // runs on the compositor.)
  const ambientRef = useAmbient();

  const categoryStyle = {
    fontFamily: "Roboto, sans-serif",
    fontSize: { xs: "11px", md: "12px" },
    color: "rgb(153, 153, 153)",
    textTransform: "uppercase",
    letterSpacing: "1px",
    mb: 0.5,
  };

  const skillStyle = {
    fontFamily: "Kalam, cursive",
    fontSize: { xs: "14px", md: "16px" },
    lineHeight: 1.7,
    color: "#333",
  };

  return (
    <section id="Skills" data-nav-bg="light" ref={ambientRef}>
      <Box sx={{ backgroundColor: "#fff" }}>
        {/* Centered Container */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "1000px",
            mx: "auto",
            px: { xs: 2, md: 3 },
            pt: { xs: 4, md: 5 },
            // Extra bottom space on mobile reserves room for the eye animation,
            // which stands on the wave seam below (absolutely positioned).
            pb: { xs: 15, md: 5 },
          }}
        >
          {/* Title. Reveal is a Box, so these swaps leave the layout untouched;
              the section root already carries the ambient ref. */}
          <Reveal sx={{ textAlign: "center", mb: 2 }}>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                textTransform: "uppercase",
                letterSpacing: { xs: "4px", md: "6px" },
                fontSize: { xs: "24px", md: "28px" },
                fontWeight: 500,
              }}
            >
              <GradientText>Tech</GradientText>
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                textTransform: "uppercase",
                letterSpacing: { xs: "4px", md: "6px" },
                fontSize: { xs: "24px", md: "28px" },
                fontWeight: 500,
              }}
            >
              <GradientText>- Skills</GradientText>
            </Typography>
          </Reveal>

          <Divider sx={{ borderColor: "rgba(0,0,0,0.1)", mb: { xs: 3, md: 4 } }} />

          {/* Intro Description */}
          <Reveal sx={{ mb: { xs: 3, md: 4 } }}>
            <Typography
              sx={{
                fontFamily: "Kalam, cursive",
                fontSize: { xs: "16px", md: "18px" },
                lineHeight: 1.7,
                color: "#333",
                textAlign: "left",
              }}
            >
              Architecture graduate from NIT Trichy turned product
              designer-engineer. I combine design-systems thinking with
              hands-on engineering to research, design, and ship intuitive
              tools for complex, data-heavy products.
            </Typography>
          </Reveal>

          {/* Skills Grid */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 3, md: 4 },
              // On desktop, stretch both columns to the same (taller) height so the
              // animation box can vertically center the eye against the skills list.
              alignItems: { xs: "flex-start", md: "stretch" },
            }}
          >
            {/* Skills List */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: { xs: 2, md: 2.5 },
                textAlign: { xs: "center", md: "right" },
              }}
            >
              <Reveal>
                <Typography sx={{ ...skillStyle, fontWeight: 600, mb: 1 }}>
                  Things <GradientText inline>I Learned</GradientText>
                </Typography>
              </Reveal>

              <Reveal>
                <Typography sx={categoryStyle}>Product &amp; UX</Typography>
                <Typography sx={skillStyle}>
                  Enterprise UX, UX Writing &amp; Content Design, User Research
                  &amp; Shadowing, Information Architecture, Progressive
                  Disclosure, Workflow &amp; State-first Design, Usability
                  Testing, Accessibility
                </Typography>
              </Reveal>

              <Reveal>
                <Typography sx={categoryStyle}>Engineering</Typography>
                <Typography sx={skillStyle}>
                  React, Next.js, TypeScript, JavaScript/ES6, HTML/CSS (SASS),
                  PWAs (offline-first), Firebase, Git, CI/CD
                </Typography>
              </Reveal>

              <Reveal>
                <Typography sx={categoryStyle}>Data &amp; Interfaces</Typography>
                <Typography sx={skillStyle}>
                  Design Systems &amp; Tokens, Storybook (50+ components), AG
                  Grid (10k+ rows, virtualized), Data Visualization, Faceted
                  Search, Dark/Light Theming
                </Typography>
              </Reveal>

              <Reveal>
                <Typography sx={categoryStyle}>Tools</Typography>
                <Typography sx={skillStyle}>
                  Figma, Storybook, Adobe Creative Suite, Material UI
                </Typography>
              </Reveal>

              <Reveal>
                <Typography sx={categoryStyle}>Range</Typography>
                <Typography sx={skillStyle}>
                  NIT-Trichy Architecture, AutoCAD / Revit / SketchUp, Adobe
                  Ps&middot;Ai
                </Typography>
              </Reveal>

              <Reveal sx={{ mt: { xs: 1, md: 1.5 } }}>
                <Typography sx={{ ...skillStyle, fontWeight: 600, mb: 1 }}>
                  What <GradientText inline>I Want to Learn</GradientText>
                </Typography>
                <Typography sx={skillStyle}>
                  Backend Depth &amp; System Design, AI / LLM Product
                  Engineering, Native Mobile (React Native / Flutter), Leading
                  Design at Scale
                </Typography>
              </Reveal>
            </Box>

            {/* Animation - desktop only: lives in the grid's right column.
                On mobile it's rendered separately, standing on the wave seam below. */}
            <Reveal
              sx={{
                flex: 1,
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                alignItems: "center",
                minHeight: { md: "250px" },
              }}
            >
              {/* maxWidth "100%" lets the eye grow to fill the whole right column
                  (instead of the 400px default); the parent's stretch + this box's
                  centering keep it vertically centered against the skills list. */}
              {isDesktop && <LucaEyeAnimation maxWidth="100%" />}
            </Reveal>
          </Box>
        </Box>
      </Box>

      {/* Wave SVG + the mobile eye standing on its seam.
          On mobile, the eye animation is absolutely anchored to the wave's wavy
          line (the white→gradient seam) so it reads as standing on the gradient,
          mirroring the footer's Luca who stands on the footer gradient. The
          `bottom` percentage tracks the wave's height across phone widths; nudge
          it up/down to sit the eye exactly on the seam. */}
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            position: "absolute",
            left: "50%",
            bottom: "62%",
            transform: "translateX(-50%)",
            width: "130px",
            justifyContent: "center",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          {!isDesktop && <LucaEyeAnimation />}
        </Box>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 220">
          <path
            fill="#ffffff"
            fillOpacity="1"
            d="M0,32L80,58.7C160,85,320,139,480,170.7C640,203,800,213,960,202.7C1120,192,1280,160,1360,144L1440,128L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z"
          ></path>
        </svg>
      </Box>
    </section>
  );
};

export default TechSkills;
